'use strict';

import BamArgValidator from 'bam-arg-validator';
import seoUrl from 'seoUrl';

/**
 * Static functions which are defaults for various options passed to the BamLinker constructor. Consumers
 * are not meant to invoke any of these functions directly, but they may be useful for reference, when overriding for
 * property-specific subclasses.
 */
class DefaultFunctions {

    /**
     * Passed a langCode, appView, or teamId, returns a path element like '/fr'.  If `identifier` and
     * `defaultIdentifier` are equal, the returned path element will be empty-string.  Values returned by this function
     * are concatenated when building URLs.  Some examples:
     * <ul>
     *   <li>Passed 'en' langCode, returns '' (since 'en' is the default)</li>
     *   <li>Passed 'fr' langCode, returns '/fr'</li>
     *   <li>Passed 'web' appView, returns '' (since 'web' is the default)</li>
     *   <li>Passed 'app' appView, returns '/app'</li>
     *   <li>Passed 0 teamId, returns '' (since 0 is the default)</li>
     *   <li>Passed 1 teamId, returns (for NHL) '/devils'</li>
     *   <li>Passed 555 teamId, throws an error, as there is no team with ID 555</li>
     * </ul>
     * @param {BamLinker} linker A BamLinker instance
     * @param {string} identifier The langCode, appView, or teamId
     * @param {string} defaultIdentifier The default langCode, appView, or teamId
     * @param {boolean} identifierIsTeamId True if `identifier` is a teamId (thus we must match it to a team and its slug), false
     *   otherwise
     * @return {string}
     */
    static getPathEl(linker, identifier, defaultIdentifier, identifierIsTeamId) {
        BamArgValidator.validateArgs(arguments, ['linker', 'identifier', 'defaultIdentifier', 'identifierIsTeamId'],
            ['object', 'string|number', 'string|number', 'boolean'], [true, true, true, true]);
        if (identifier === defaultIdentifier) {
            return '';
        }
        let segment;
        if (!identifierIsTeamId) {
            segment = identifier;
        } else {
            segment = linker._teamData.getTeamSlug && linker._teamData.getTeamSlug(identifier);
            if (segment === null) {
                throw new Error('There is no team with ID ' + identifier);
            }
        }
        return '/' + segment;
    }

    /**
     * Builds a generic link.  This function is called by BamLinker.getLink() after that method does a small
     * amount of validation.  Returns concatenation of the following values (all of which may be empty-string except for
     * `urlSegment`, which minimally has a length of 1 ('/').  The values:
     * <ul>
     *     <li>an appView segment such as '/app' or ''</li>
     *     <li>a langCode segment such as '/fr' or ''</li>
     *     <li>a team segment such as '/rangers' or ''</li>
     *     <li>The `urlSegment` value</li>
     * </ul>
     * Thus URLs returned by this function could be as simple as '/' and as complex as
     * http://www.somehost.com/app/fr/rangers/somesection/someparam/someparam
     * @param {BamLinker} linker A BamLinker instance
     * @param {string} urlSegment The segment to append to the base URL, e.g. "/player/some-player-121212". Segments
     *   which don't
     *   start with a forward-slash get one prepended automatically.
     * @param {boolean} keepTeam If true and the current request's teamId is not zero (meaning, there's currently a
     *   team segment
     *   on the URL), that segment will be preserved for the returned URL.  If false, it won't be. The default is true.
     * @return {string}
     */
    static buildLink(linker, urlSegment, keepTeam) {
        BamArgValidator.validateArgs(arguments, ['linker', 'urlSegment', 'keepTeam'],
            ['object', 'string', 'boolean'], [true, true, false]);

        var url = linker._appViewPathEl
            + linker._langCodePathEl
            + (keepTeam ? linker._teamPathEl : '')
            + urlSegment;
        return url;
    }

    /**
     * Builds a player-page link in the format '/player/playerSlug', with no team segment preserved.  The potentially
     * redundant statsapiPerson and fullname/id fields are passed straight through to the _buildPlayerSlugFn; see
     * its API docs for details.
     * @param {BamLinker} linker A BamLinker instance
     * @param {object | null} statsapiPerson
     * @param {string} fullname
     * @param {string | number} playerId
     * @param {string | number} A team ID; whether required/used depends on property
     * @return {string} A player-page URL as returned by `buildLink()`
     */
    static buildPlayerPageLink(linker, statsapiPerson, fullname, playerId, playerTeamId) {
        BamArgValidator.validateArgs(arguments, ['linker', 'statsapiPerson', 'fullname', 'playerId', 'playerTeamId'],
            ['object', 'object|null', 'string', 'string|number', 'string|number'], [true, true, true, true, false]);
        return linker._buildLinkFn(linker, '/player/' + linker._buildPlayerSlugFn(linker, statsapiPerson,
                fullname, playerId), false);
    }

    /**
     * Build a player slug in the format first-last-12345678 based on either the statsapiPerson param, or the fullname
     * and id params.  Specifically, if the statsapiPerson object contains a slug value (by default in this base class,
     * otherNames.slug), we'll use that.  If not, we'll derive one from the fullname and id.  This way, we rely on
     * statsapi as the single source of truth for slugs _if they're providing that field_.  If not, we fall back on
     * deriving it ourselves.
     * @param linker A BamLinker instance
     * @param {object | null} statsapiPerson, or null if the caller doesn't have one to pass.  If the statsapiPerson
     *   doesn't have a otherNames.slug value, that's fine; fullname and playerId will be used instead.
     * @param {string} fullname
     * @param {string | number} playerId
     * @return {string} E.g. first-last-12345678
     */
    static buildPlayerSlug(linker, statsapiPerson, fullname, playerId) {
        BamArgValidator.validateArgs(arguments, ['linker', 'statsapiPerson', 'fullname', 'playerId'],
            ['object', 'object|null', 'string', 'string|number'], [true, true, true, true]);
        if (statsapiPerson && statsapiPerson.otherNames && statsapiPerson.otherNames.slug) {
            // The API had a slug value:
            return statsapiPerson.otherNames.slug;
        }

        // The API _didn't_ have a slug value:
        return seoUrl(fullname + ' ' + playerId);
    }

    /**
     * Build a team homepage link; likely property-dependent - the default here is the format "/teamslug"
     * @param linker {BamLinker} A BamLinker instance
     * @param {string | number} teamId The ID of the team whose homepage to link to.
     * @return {string} A team-homepage URL as returned by `buildLink()`, or null if unrecognized active team ID.
     */
    static buildTeamHomepageLink(linker, teamId) {
        let slug = linker._teamData.getTeamSlug && linker._teamData.getTeamSlug(teamId);
        if (!slug) {
            return null;
        }

        return linker._buildLinkFn(linker, '/' + slug, false);
    }

    /**
     * Build a team schedule link; likely property-dependent - the default here is the format "/schedule/teamslug"
     * @param linker {BamLinker} A BamLinker instance
     * @param {string | number} teamId The ID of the team whose schedule to link to.
     * @return {string} A team-schedule URL as returned by `buildLink()`, or null if unrecognized active team ID.
     */
    static buildTeamScheduleLink(linker, teamId) {
        let slug = linker._teamData.getTeamSlug && linker._teamData.getTeamSlug(teamId);

        if (!slug) {
            return null;
        }

        return linker._buildLinkFn(linker, `/${slug}/schedule`, false);
    }

}

export default DefaultFunctions;
